import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, ChevronDown, ChevronLeft, ChevronRight, Wind } from "lucide-react";
import { AURIA_INTENT_LIST, getAuriaIntent } from "../../services/ai/domain/intents.js";
import { normalizeDailyClassicDuration, normalizeDailyIntent, pickNextDailyBreathingId, rememberDailyBreathingSelection } from "../../services/breathing/dailyBreathing.js";
import { BONUS_BREATHING_ID, readUnlockedRewards } from "../../services/rewards/streakRewards.js";

const INTENT_OPTIONS = AURIA_INTENT_LIST.map((intent) => ({
  key: intent.key,
  label: intent.label,
  hint: intent.hint,
  shortLabel: intent.key === "relax" ? "Détente" : intent.key === "rest" ? "Repos" : "Recentrage",
}));

const CLASSIC_DURATIONS = Object.freeze([
  { key: "30sec", label: "30 secondes", shortLabel: "30 sec", premium: false },
  { key: "2min", label: "2 minutes", shortLabel: "2 min", premium: false },
  { key: "5min", label: "5 minutes", shortLabel: "5 min", premium: true },
]);

const SOUND_COPY = Object.freeze({
  1: { name: "Cohérence cardiaque", rhythm: "6 s inspire · 6 s expire", reason: "Un rythme régulier pour revenir progressivement au calme." },
  2: { name: "Respiration carrée", rhythm: "4 s inspire · 4 pause · 4 expire · 4 pause", reason: "Un cadre stable pour ralentir les pensées." },
  3: { name: "Soupir physiologique", rhythm: "Grande inspiration · longue expiration", reason: "Une respiration directe pour relâcher rapidement la pression." },
  4: { name: "Longue expiration", rhythm: "3 s inspire · 7 s expire", reason: "Une expiration prolongée pour aider le corps à ralentir." },
  5: { name: "Respiration 4-7-8", rhythm: "4 s inspire · 7 pause · 8 expire", reason: "Un rythme lent pour accompagner la transition vers le repos." },
  6: { name: "Respiration alternée", rhythm: "3 s inspire · 3 s expire · en alternance", reason: "Une alternance douce pour apaiser l’agitation mentale." },
  7: { name: "Respiration triangle", rhythm: "4 s inspire · 4 pause · 8 expire", reason: "Un rythme en trois temps pour rassembler l’attention." },
  8: { name: "Boosteur d’énergie", rhythm: "Respiration rapide · 10 s de pause", reason: "Une respiration dynamique pour réveiller l’attention." },
  9: { name: "Grande ventilation", rhythm: "8 s inspire · 8 s expire, en 3 paliers", reason: "Une respiration ample pour retrouver une présence plus nette." },
});

function getSoundName(sound) {
  return SOUND_COPY[sound?.id]?.name || sound?.name || "Respiration";
}

function getSoundReason(sound) {
  return SOUND_COPY[sound?.id]?.reason || sound?.description || "";
}

function getSoundRhythm(sound) {
  return SOUND_COPY[sound?.id]?.rhythm || sound?.breathingPattern || "";
}

function announcePlayerTransition() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("harmonauria:player-transition-start"));
}

export default function BreathingFlow({ sounds = [], user = null, isPremium = false, premiumSoundIds = [], naturalSounds = [], favoriteNatureSoundIds = [], preferredIntent = "", dailyTime = null, onStart, onLockedDuration, onLockedSound, onLockedAuria, onBeforePlayerLaunch, onPrepareAuriaSession, onBackHome, onStepChange, useTopbarNavigation = false }) {
  const navigate = useNavigate();
  const profileIntentKey = normalizeDailyIntent(preferredIntent);
  const profileClassicDurationKey = normalizeDailyClassicDuration(dailyTime, isPremium);
  const [intentKey, setIntentKey] = useState(profileIntentKey);
  const [durationKey, setDurationKey] = useState(profileClassicDurationKey);
  const [settingsOpen, setSettingsOpen] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLaunchingClassic, setIsLaunchingClassic] = useState(false);
  const carouselRef = useRef(null);
  const carouselRafRef = useRef(0);
  const launchGuardRef = useRef(false);

  const premiumSoundIdSet = useMemo(() => new Set(premiumSoundIds), [premiumSoundIds]);
  const intentConfig = useMemo(() => getAuriaIntent(intentKey || "relax"), [intentKey]);
  const intentSounds = useMemo(() => (intentConfig?.breathingIds || []).map((id) => sounds.find((sound) => sound.id === id)).filter(Boolean), [intentConfig, sounds]);
  const accessibleIds = useMemo(() => intentSounds.filter((sound) => isPremium || !premiumSoundIdSet.has(sound.id)).map((sound) => sound.id), [intentSounds, isPremium, premiumSoundIdSet]);
  const recommendedSoundId = useMemo(() => {
    if (!intentSounds.length) return null;
    const nextSoundId = pickNextDailyBreathingId(user?.uid, intentKey, accessibleIds);
    return nextSoundId ?? accessibleIds[0] ?? intentSounds[0]?.id ?? null;
  }, [accessibleIds, intentKey, intentSounds, user?.uid]);
  const displayedIntentSounds = useMemo(() => {
    if (!recommendedSoundId) return intentSounds;
    const recommended = intentSounds.find((sound) => sound.id === recommendedSoundId);
    if (!recommended) return intentSounds;
    return [recommended, ...intentSounds.filter((sound) => sound.id !== recommendedSoundId)];
  }, [intentSounds, recommendedSoundId]);
  const activeSound = displayedIntentSounds[activeIndex] || displayedIntentSounds[0] || null;
  const activeSoundLocked = Boolean(activeSound && premiumSoundIdSet.has(activeSound.id) && !isPremium);
  const intentOption = INTENT_OPTIONS.find((option) => option.key === intentKey) || INTENT_OPTIONS[0];
  const durationOption = CLASSIC_DURATIONS.find((option) => option.key === durationKey) || CLASSIC_DURATIONS[1];
  const bonusSound = useMemo(() => sounds.find((sound) => sound.id === BONUS_BREATHING_ID) || null, [sounds]);
  const bonusUnlocked = readUnlockedRewards(user).breathing;

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.classList.add("breathing-flow-active");
    body.classList.add("breathing-flow-active");
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    return () => {
      html.classList.remove("breathing-flow-active");
      body.classList.remove("breathing-flow-active");
      window.cancelAnimationFrame(carouselRafRef.current);
    };
  }, []);

  useEffect(() => {
    setIntentKey(profileIntentKey);
    setDurationKey(profileClassicDurationKey);
  }, [profileClassicDurationKey, profileIntentKey]);

  const scrollToIndex = useCallback((index, behavior = "smooth") => {
    const carousel = carouselRef.current;
    const target = carousel?.children?.[index];
    if (!carousel || !target) return;
    const left = target.offsetLeft - (carousel.clientWidth - target.clientWidth) / 2;
    carousel.scrollTo({ left: Math.max(0, left), behavior });
  }, []);

  useEffect(() => {
    setActiveIndex(0);
    setSettingsOpen("");
    const carousel = carouselRef.current;
    if (carousel) carousel.scrollLeft = 0;
  }, [intentKey, recommendedSoundId]);

  const handleCarouselScroll = useCallback(() => {
    window.cancelAnimationFrame(carouselRafRef.current);
    carouselRafRef.current = window.requestAnimationFrame(() => {
      const carousel = carouselRef.current;
      if (!carousel?.children?.length) return;
      const center = carousel.scrollLeft + carousel.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      [...carousel.children].forEach((child, index) => {
        const childCenter = child.offsetLeft + child.clientWidth / 2;
        const distance = Math.abs(childCenter - center);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setActiveIndex(closestIndex);
    });
  }, []);

const chooseIntent = useCallback((nextIntentKey) => {
  setIntentKey(nextIntentKey);
}, []);

  const chooseDuration = useCallback((duration) => {
    if (duration.premium && !isPremium) {
      onLockedDuration?.(duration.key);
      return;
    }

    setDurationKey(duration.key);
    setSettingsOpen("");
  }, [isPremium, onLockedDuration]);

  const startClassic = useCallback(() => {
    if (launchGuardRef.current || isLaunchingClassic || !activeSound) return;

    if (activeSoundLocked) {
      onLockedSound?.(activeSound.id);
      return;
    }

    launchGuardRef.current = true;
    setIsLaunchingClassic(true);
    rememberDailyBreathingSelection(user?.uid, intentKey, accessibleIds, activeSound.id);
    const started = onStart?.({ sound: activeSound, durationKey, intent: intentKey });
    if (!(started && typeof started.then === "function")) announcePlayerTransition();

    window.setTimeout(() => {
      setIsLaunchingClassic(false);
      launchGuardRef.current = false;
    }, 500);
  }, [accessibleIds, activeSound, activeSoundLocked, durationKey, intentKey, isLaunchingClassic, onLockedSound, onStart, user?.uid]);

  const startBonus = useCallback(() => {
    if (launchGuardRef.current || isLaunchingClassic || !bonusSound) return;

    launchGuardRef.current = true;
    setIsLaunchingClassic(true);
    const started = onStart?.({ sound: bonusSound, durationKey, intent: intentKey });
    if (!(started && typeof started.then === "function")) announcePlayerTransition();

    window.setTimeout(() => {
      setIsLaunchingClassic(false);
      launchGuardRef.current = false;
    }, 500);
  }, [bonusSound, durationKey, intentKey, isLaunchingClassic, onStart]);

const goBack = useCallback(() => {
  if (onBackHome) {
    onBackHome();
    return;
  }

  navigate(-1);
}, [navigate, onBackHome]);

  const renderClassic = () => (
    <section
      id="breathing-result-panel"
      className="breathing-flow__result-section breathing-flow__manual breathing-flow__classic"
    >
<header className="breathing-flow__classic-heading">
  <p className="breathing-flow__classic-eyebrow">Respiration guidée</p>
  <h1 id="breathing-flow-title">Votre respiration</h1>
  <p className="breathing-flow__classic-intro">Swipez pour découvrir les trois respirations liées à votre objectif.</p>
</header>



      <div className="breathing-flow__carousel-shell">
        <button
          type="button"
          className="breathing-flow__carousel-arrow breathing-flow__carousel-arrow--prev"
          onClick={() => {
            const nextIndex = Math.max(0, activeIndex - 1);
            setActiveIndex(nextIndex);
            scrollToIndex(nextIndex);
          }}
          disabled={activeIndex === 0}
          aria-label="Voir la respiration précédente"
        >
          <ChevronLeft size={24} strokeWidth={2.2} aria-hidden="true" />
        </button>

        <div
          ref={carouselRef}
          className="breathing-flow__breathing-options breathing-flow__breathing-carousel"
          onScroll={handleCarouselScroll}
          aria-label="Respirations proposées"
        >
          {displayedIntentSounds.map((sound) => {
            const locked = premiumSoundIdSet.has(sound.id) && !isPremium;
            const recommended = sound.id === recommendedSoundId;

            return (
              <article
                key={sound.id}
                className={`breathing-flow__breathing-option breathing-flow__breathing-slide ${locked ? "is-locked" : ""}`}
                aria-label={`${getSoundName(sound)}${locked ? ", incluse dans le Refuge Personnel" : ""}`}
              >
                <div className="breathing-flow__breathing-option-copy">
                  <span className="breathing-flow__slide-topline">
                    {recommended ? "Proposée pour vous" : "Autre respiration"}
                  </span>
                  <h2 className="breathing-flow__breathing-option-title">{getSoundName(sound)}</h2>
                  <p className="breathing-flow__breathing-option-rhythm">{getSoundRhythm(sound)}</p>
                  <p className="breathing-flow__breathing-option-description">{getSoundReason(sound)}</p>
                </div>

                {locked && (
                  <span className="breathing-flow__choice-refuge breathing-flow__slide-refuge">Refuge</span>
                )}
              </article>
            );
          })}
        </div>

        <button
          type="button"
          className="breathing-flow__carousel-arrow breathing-flow__carousel-arrow--next"
          onClick={() => {
            const nextIndex = Math.min(displayedIntentSounds.length - 1, activeIndex + 1);
            setActiveIndex(nextIndex);
            scrollToIndex(nextIndex);
          }}
          disabled={activeIndex >= displayedIntentSounds.length - 1}
          aria-label="Voir la respiration suivante"
        >
          <ChevronRight size={24} strokeWidth={2.2} aria-hidden="true" />
        </button>
      </div>

      <div className="breathing-flow__carousel-dots" aria-label="Choisir une respiration">
        {displayedIntentSounds.map((sound, index) => (
          <button
            key={sound.id}
            type="button"
            className={`breathing-flow__carousel-dot ${activeIndex === index ? "is-active" : ""}`}
            onClick={() => {
              setActiveIndex(index);
              scrollToIndex(index);
            }}
            aria-label={`Voir ${getSoundName(sound)}`}
            aria-current={activeIndex === index ? "true" : undefined}
          />
        ))}
      </div>

      <button
        id="tour-cta"
        type="button"
        className={activeSoundLocked ? "breathing-flow__refuge-start breathing-flow__refuge-start--soft" : "breathing-flow__start"}
        onClick={startClassic}
        disabled={!activeSound || isLaunchingClassic}
      >
        {isLaunchingClassic ? "Lancement…" : activeSoundLocked ? "Découvrir le Refuge Personnel" : "Commencer"}
        {!isLaunchingClassic && <ArrowRight size={17} strokeWidth={2.4} aria-hidden="true" />}
      </button>
      <div className="breathing-flow__session-settings" aria-label="Ajuster ce moment">
        <button
          type="button"
          className="breathing-flow__choice breathing-flow__setting-pill"
          onClick={() => setSettingsOpen((current) => current === "intent" ? "" : "intent")}
          aria-expanded={settingsOpen === "intent"}
        >
          <span className="breathing-flow__setting-copy">
            <small>Objectif</small>
            <strong>{intentOption.shortLabel}</strong>
          </span>
          <ChevronDown
            size={17}
            strokeWidth={2.2}
            aria-hidden="true"
          />
        </button>

        <button
          type="button"
          className="breathing-flow__choice breathing-flow__setting-pill"
          onClick={() => setSettingsOpen((current) => current === "duration" ? "" : "duration")}
          aria-expanded={settingsOpen === "duration"}
        >
          <span className="breathing-flow__setting-copy">
            <small>Durée</small>
            <strong>{durationOption.shortLabel}</strong>
          </span>
          <ChevronDown
            size={17}
            strokeWidth={2.2}
            aria-hidden="true"
          />
        </button>
      </div>

      {settingsOpen === "intent" && (
        <div className="breathing-flow__setting-panel" aria-label="Choisir un objectif pour cette séance">
          {INTENT_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`breathing-flow__choice breathing-flow__setting-option ${intentKey === option.key ? "is-selected" : ""}`}
              aria-pressed={intentKey === option.key}
              onClick={() => chooseIntent(option.key)}
            >
              <span>
                <strong>{option.label}</strong>
                <small>{option.hint}</small>
              </span>
              {intentKey === option.key && <Check size={17} strokeWidth={2.4} aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}

      {settingsOpen === "duration" && (
        <div className="breathing-flow__setting-panel breathing-flow__setting-panel--duration" aria-label="Choisir une durée pour cette séance">
          {CLASSIC_DURATIONS.map((duration) => {
            const locked = duration.premium && !isPremium;

            return (
              <button
                key={duration.key}
                type="button"
                className={`breathing-flow__choice breathing-flow__setting-option breathing-flow__setting-option--duration ${durationKey === duration.key ? "is-selected" : ""} ${locked ? "is-locked" : ""}`}
                aria-pressed={durationKey === duration.key}
                onClick={() => chooseDuration(duration)}
              >
                <strong>{duration.label}</strong>
{locked ? (
  <span className="breathing-flow__choice-refuge">
    Refuge
  </span>
) : durationKey === duration.key ? (
                  <Check size={17} strokeWidth={2.4} aria-hidden="true" />
                ) : null}
              </button>
            );
          })}
        </div>
      )}
      {bonusUnlocked && bonusSound && (
        <button
          type="button"
          className="breathing-flow__bonus-line"
          onClick={startBonus}
          disabled={isLaunchingClassic}
        >
          <Wind size={16} strokeWidth={2} className="breathing-flow__bonus-line-mark" aria-hidden="true" />
          <span className="breathing-flow__bonus-line-copy">
            <strong>Oxygénation de l’organisme</strong>
            <small>Votre respiration en plus, ouverte par votre régularité</small>
          </span>
          <ArrowRight size={18} strokeWidth={2.2} className="breathing-flow__bonus-line-arrow" aria-hidden="true" />
        </button>
      )}
    </section>
  );

  return (
    <section
      className="breathing-flow breathing-flow--result breathing-flow--custom"
      aria-labelledby="breathing-flow-title"
    >
      {!useTopbarNavigation && (
        <button
          type="button"
          className="breathing-flow__back"
          onClick={goBack}
          aria-label="Revenir à la page d’accueil"
        >
          <ArrowLeft size={20} strokeWidth={2} />
          <span>Retour</span>
        </button>
      )}

      <div
        className="breathing-flow__stage"
        aria-live="polite"
      >
        <div className="breathing-flow__result-page breathing-flow__result-page--classic-only">
          <div className="breathing-flow__result-content">
            {renderClassic()}
          </div>
        </div>
      </div>
    </section>
  );
}
